import React from 'react';
import SendIcon from '@mui/icons-material/Send';
import { green } from '@mui/material/colors';
import { Avatar, Button, Card, CardContent, CardHeader, Grid, TextField } from '@mui/material';
import { DateHelper, verifyLength } from '../../../Component/Model/helpers';
import { MFARequest, SecurityInfo } from '../../../Component/Model/SignIn';
import { Auth } from '../../../Component/Api/Auth/AuthService';
import { LoadingButton } from '@mui/lab';
import { showConfirmDialog } from '../../../Component/Common/Components/SweetAlert/SweetAlert';
import { toast } from 'react-toastify';

interface IProps {
    ShowAddTwoFactorAuthenticationDevice: boolean;
    ShowCancelAddTwoFactorAuthenticationDevice: boolean;
    ShowCurrentTwoFactorAuthenticationConfiguration: boolean;
    LoadSecurityData: (data: SecurityInfo) => void;
    securityInfo: SecurityInfo;
    TwoFactorButtonText: string;

    OnCancelAddTwoFactorAuthenticationDeviceClick: () => void;
    CancelTwoFactorCard: () => void;
    OnChangeTwoFactorAuthenticationDeviceClick: () => void;
    handleTFARequest: (data: MFARequest) => void;
}

export const TwoFactorContent = (props: IProps) => {
    const [TwoFactorCodeState, setTwoFactorCodeState] = React.useState<string>("");
    const [TwoFactorCode, setTwoFactorCode] = React.useState<string>("");
    const [loadingRequest, setLoadingRequest] = React.useState<boolean>(false);

    const handleMFAAuthentication = () => {
        if (TwoFactorCode === "") {
            setTwoFactorCodeState("error");
            return;
        }
        const request: MFARequest = {
            Code: TwoFactorCode,
            SecretKey: props.securityInfo.SecretKey,
            EnableTwoFactorAuthenticationRememberMe: props.securityInfo.EnableTwoFactorAuthenticationRememberMe
        };
        setLoadingRequest(true);
        try {
            props.handleTFARequest(request);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingRequest(false);
        }
    }

    const DisableTwoFactorWarning = () => {
        showConfirmDialog('Confirm Disable', 'Are you sure you want to disable Two Factor Authentication? This action can not be undone.', 'Disable', () => handleDisable());
    }

    const handleDisable = async () => {
        try {
            const result = await Auth.disableTwoFactorAuthentication();
            if (result.status === 200) {
                props.LoadSecurityData(result.data);
                toast.success('Two Factor Authentication Disabled');
            }
        } catch (error) {
            console.log(error);
        }

    }

    return (
        <Grid item xs={12}>
            {props.ShowAddTwoFactorAuthenticationDevice ?
                <Card>
                    <CardHeader
                        title="Two Factor Authentication"
                        avatar={<Avatar sx={{ bgcolor: green[500] }}>2FA</Avatar>}
                    >
                    </CardHeader>
                    <CardContent>
                        <Grid container>
                            <Grid item xs={12}>
                                <h4>1. Add BEE123 to an authenticator app</h4>
                                <p>Download a TOTP-compatible authenticator app for your mobile phone (e.g. Google Authenticator, Microsoft Authenticator):</p>
                                <span style={{ marginLeft: "10px" }}>
                                    <a target="_blank" rel="noreferrer" href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"><img style={{ width: '200px' }} alt="Get it on Google Play" src={`${process.env.PUBLIC_URL}/img/google-play-badge.png`} /></a>
                                    <a target="_blank" rel="noreferrer" href="https://itunes.apple.com/za/app/google-authenticator/id388497605?mt=8"><img style={{ width: '200px' }} alt="Get it on the App Store" src={`${process.env.PUBLIC_URL}/img/apps-apple-appstore-logo.svg`} /></a>
                                </span>
                                <p>
                                    Open your authenticator app and add BEE123 by scanning the QR Code.
                                </p>
                                <img src={`data:image/png;base64, ${props.securityInfo.QRCodeImage}`} alt="QR" style={{ width: '200px', height: '200px' }} />
                                <h4>2. Enter the 6 digit code that your authenticator app generates</h4>
                                <p>
                                    Verify that BEE123 is added correctly by entering the 6 digit code generated by your authenticator app below, 
                                    and then click "Enable Two Factor Authentication".
                                </p>
                                <Grid item xs={4} >
                                    <TextField
                                        error={TwoFactorCodeState === "error"}
                                        helperText={TwoFactorCodeState === "error" ? "Invalid Code" : ""}
                                        label="QR code"
                                        name="QRCode"
                                        type="text"
                                        onChange={(event: any) => {
                                            if (verifyLength(event.target.value, 6)) {
                                                setTwoFactorCodeState("success");
                                            } else {
                                                setTwoFactorCodeState("error")
                                            }
                                            setTwoFactorCode(event.target.value);
                                        }}
                                    />
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid container mt={2}>
                            <Grid item xs={12} sm={12} md={6}>
                                <LoadingButton color="primary" onClick={handleMFAAuthentication} endIcon={<SendIcon />} loading={loadingRequest}
                                    loadingPosition="end"
                                    variant="contained">
                                    {props.TwoFactorButtonText}
                                </LoadingButton>
                            </Grid>
                            {props.ShowCancelAddTwoFactorAuthenticationDevice ?
                                <Grid item xs={12} sm={12} md={6}>
                                    <Button variant="contained" onClick={() => { props.OnCancelAddTwoFactorAuthenticationDeviceClick() }}>
                                        Cancel
                                    </Button>
                                </Grid>
                                : <Grid item xs={12} sm={12} md={6}>
                                    <Button variant="contained" onClick={() => { props.CancelTwoFactorCard() }}>
                                        Cancel
                                    </Button>
                                </Grid>}

                        </Grid>
                    </CardContent>
                </Card>
                : null}

            {props.ShowCurrentTwoFactorAuthenticationConfiguration ?
                <Card>
                    <CardHeader
                        title="Two Factor Authentication : Active"
                        avatar={<Avatar>2FA</Avatar>}
                    >
                    </CardHeader>
                    <CardContent>
                        <Grid container>
                            <Grid item xs={12}>
                                <h4>Two Factor Device added: {DateHelper(new Date(props.securityInfo.DeviceAddedDateTime))}</h4>
                            </Grid>
                            <Grid item xs={12}>
                                <h2>Active Sessions</h2>
                                {(props.securityInfo.RememberMeDetails !== null && props.securityInfo.RememberMeDetails.length > 0) ?
                                    <Grid item xs={6}>
                                        <ul>
                                            {props.securityInfo.RememberMeDetails.map((rememberMe: any, index: number) => {
                                                return <li key={index}>{rememberMe.DeviceDetails}
                                                    {rememberMe.IsCurrentSession ? <span style={{ float: "right" }}>&nbsp;Current Session</span> : null}
                                                    <span style={{ float: "right" }}>{DateHelper(new Date(rememberMe.LastLoggedIn))} </span>

                                                </li>
                                            })}
                                        </ul>
                                    </Grid> : <p>There are no active remember me sessions</p>}
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <Button variant="contained" color="primary" onClick={() => { props.OnChangeTwoFactorAuthenticationDeviceClick() }}>
                                    Change Device
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <Button variant="contained" color="secondary" onClick={() => { DisableTwoFactorWarning() }}>
                                    Disable Two Factor Authentication
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
                : null}
        </Grid>
    )
}
